/**
 * Brain Bar tool — `draft_player_briefs`.
 *
 * Given the current tactics layout, runs the rule-based brief engine
 * (`generateAllPlayerBriefs` from `@4cm/core/tactics`) and returns the
 * 11 briefs for the requested side. The drawer on the client side picks
 * the draft up and writes it through `saveTacticsLayout`.
 *
 * v1 is rule-only — Phase 5 will accept an optional `polish: true` flag
 * to run each rule brief through Claude for prose smoothing. Until
 * then, the tool stays deterministic + offline-safe (§14.2 fallback).
 */

import type { AnthropicTool } from '@4cm/core/ai';
import {
  generateAllPlayerBriefs,
  type PlayerBrief,
  type TacticsLayout,
} from '@4cm/core/tactics';

export const PLAYER_BRIEFS_DRAFT_TOOL: AnthropicTool = {
  name: 'draft_player_briefs',
  description:
    'Generate per-player tactical briefs from the active tactics layout. ' +
    'Each player gets a 4-section brief (in possession / out of possession / ' +
    'transitions / set pieces) derived from their position slot + named role + ' +
    'team tactics (build-up + defensive approach + sliders) + any arrows bound ' +
    'to them + any set-piece routine they appear in. Use whenever the coach asks ' +
    'to "make briefs", "generate player instructions", or "what should each ' +
    'player do on this tactic". Reads the current board from screen context; ' +
    'returns a draft the coach can commit from the briefs drawer.',
  input_schema: {
    type: 'object',
    properties: {
      side: {
        type: 'string',
        enum: ['home', 'away'],
        description:
          'Which team to brief. Defaults to "home" — only set "away" when the coach is ' +
          'managing the opposition (rare).',
      },
    },
  },
};

export const PLAYER_BRIEFS_TOOL_NAMES = new Set<string>([
  'draft_player_briefs',
]);

export const PLAYER_BRIEFS_TOOLS: AnthropicTool[] = [
  PLAYER_BRIEFS_DRAFT_TOOL,
];

export interface PlayerBriefsDraftInput {
  side?: 'home' | 'away';
}

export interface PlayerBriefsDraftPayload {
  side: 'home' | 'away';
  briefs: PlayerBrief[];
}

/**
 * Run the rule engine over `layout` and return the result wrapped in a
 * draft payload the client can commit. Pure function — caller resolves
 * the active layout (typically via `ctx.getTacticsLayout()`).
 *
 * Returns `null` when the layout is missing — the dispatcher converts
 * that to a `{ error }` response.
 */
export function dispatchPlayerBriefsTool(
  input: PlayerBriefsDraftInput,
  layout: TacticsLayout | null,
): PlayerBriefsDraftPayload | null {
  if (!layout) return null;
  const side: 'home' | 'away' = input?.side === 'away' ? 'away' : 'home';
  const briefs = generateAllPlayerBriefs(layout, side);
  return { side, briefs };
}
